import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { LayoutGrid, Search, X } from 'lucide-react';
import resourcesService from '../../services/resourcesService';
import { findPage } from '../../utils/resourceMenu.mjs';
import ResourceMenu from './ResourceMenu';
import './ResourcesNav.css';

const SIDEBAR_QUERY = '(min-width: 1180px)';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled])';

const useMediaQuery = query => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = event => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);
  return matches;
};

const Drawer = ({ onClose, focusSearch, searchRef, children }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    (focusSearch ? searchRef.current : closeRef.current)?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous && document.contains(previous)) previous.focus();
    };
  }, [focusSearch, searchRef]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const inside = panelRef.current.contains(document.activeElement);
      if (!inside || (event.shiftKey && document.activeElement === first)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="arn-drawer-root">
      <div className="arn-scrim" onClick={onClose} aria-hidden="true" />
      <div className="arn-drawer" role="dialog" aria-modal="true" aria-label="Agent Resources menu" ref={panelRef}>
        <header className="arn-drawer-head">
          <h2>Agent Resources</h2>
          <button type="button" ref={closeRef} className="arn-icon-btn" aria-label="Close menu" onClick={onClose}><X size={20} /></button>
        </header>
        {children}
      </div>
    </div>,
    document.body
  );
};

const ResourcesLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentSlug = useMatch('/agent/resources/:slug')?.params.slug || null;
  const wide = useMediaQuery(SIDEBAR_QUERY);
  const [data, setData] = useState({ categories: [], articles: [], status: 'loading' });
  const [query, setQuery] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [offsets, setOffsets] = useState({ top: 0, left: 0, right: 0, header: 0 });
  const shellRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    let active = true;
    setData(previous => ({ ...previous, status: 'loading' }));
    Promise.all([resourcesService.getCategories(), resourcesService.getArticles()])
      .then(([categories, articles]) => { if (active) setData({ categories, articles, status: 'ready' }); })
      .catch(() => { if (active) setData(previous => ({ ...previous, status: 'error' })); });
    return () => { active = false; };
  }, [reloadKey]);

  useLayoutEffect(() => {
    const measure = () => {
      const parent = shellRef.current?.parentElement;
      if (!parent) return;
      const styles = getComputedStyle(parent);
      const header = document.querySelector('main > header');
      setOffsets({
        top: parseFloat(styles.paddingTop) || 0,
        left: parseFloat(styles.paddingLeft) || 0,
        right: parseFloat(styles.paddingRight) || 0,
        header: header ? header.offsetHeight : 0
      });
    };
    measure();
    window.addEventListener('resize', measure);
    const header = document.querySelector('main > header');
    const observer = header && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(header);
    return () => { window.removeEventListener('resize', measure); if (observer) observer.disconnect(); };
  }, [wide]);

  const open = !wide && Boolean(location.state?.arnMenu);
  const openMenu = focus => navigate(`${location.pathname}${location.search}`, { state: { arnMenu: true, arnFocus: focus } });
  const closeMenu = useCallback(() => {
    if ((window.history.state?.idx || 0) > 0) navigate(-1);
    else navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [navigate, location.pathname, location.search]);

  const current = findPage(data.articles, currentSlug);
  const crumbTop = current ? (current.feed_page ? 'News & Promotions' : current.category_name) : 'Agent Resources';
  const crumbTitle = current ? current.title : (currentSlug ? 'Resource' : 'All resources');

  const menu = data.status === 'error'
    ? <div className="arn-note" role="alert">The menu could not be loaded. <button type="button" onClick={() => setReloadKey(value => value + 1)}>Retry</button></div>
    : data.status === 'loading' && !data.articles.length
      ? <div className="arn-note">Loading menu&hellip;</div>
      : (
        <ResourceMenu
          categories={data.categories}
          articles={data.articles}
          currentSlug={currentSlug}
          query={query}
          onQuery={setQuery}
          searchRef={searchRef}
          replace={open}
        />
      );

  return (
    <div className={`arn-shell ${wide ? 'wide' : ''}`} ref={shellRef}>
      {wide ? (
        <aside className="arn-sidebar" aria-label="Agent Resources menu" style={{ top: offsets.header + 16, maxHeight: `calc(100vh - ${offsets.header + 32}px)` }}>
          {menu}
        </aside>
      ) : (
        <div className="arn-bar" style={{ top: offsets.header, marginTop: -offsets.top, marginLeft: -offsets.left, marginRight: -offsets.right }}>
          <button type="button" className="arn-browse" aria-haspopup="dialog" aria-expanded={open} onClick={() => openMenu()}>
            <LayoutGrid size={18} aria-hidden="true" /> Browse
          </button>
          <div className="arn-crumb" aria-live="polite">
            <span>{crumbTop}</span>
            <strong>{crumbTitle}</strong>
          </div>
          <button type="button" className="arn-icon-btn" aria-label="Search resources" onClick={() => openMenu('search')}><Search size={20} /></button>
        </div>
      )}
      <div className="arn-main"><Outlet /></div>
      {open && <Drawer onClose={closeMenu} focusSearch={location.state?.arnFocus === 'search'} searchRef={searchRef}>{menu}</Drawer>}
    </div>
  );
};

export default ResourcesLayout;
